import { useEffect, useRef, useState } from "react";
import { VideoRoom } from "protogen/video_pb";
import { TranscriptionSegment } from "protogen/common_pb";
import { useGetVideoTranscriptions } from "services/video";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";

type TranscriptionState = {
  segments: Array<TranscriptionSegment>;
  complete: boolean;
  streamStarted: boolean;
  transcriptionFailed: boolean;
  initialLoadComplete: boolean;
  lastStableTimestamp: number;
};

const _defaultState = (): TranscriptionState => {
  return {
    segments: [],
    complete: false,
    streamStarted: false,
    transcriptionFailed: false,
    initialLoadComplete: false,
    lastStableTimestamp: -1,
  };
};

type Output = {
  segments: TranscriptionSegment[];
  complete: boolean;
  streamStarted: boolean;
  transcriptionFailed: boolean;
};

export default (videoRoom: VideoRoom, tracks: string[] = []): Output => {
  const { request } = useGetVideoTranscriptions();
  const { registerHandler, unregisterHandler } = useWebSocketContext();

  const [state, setState] = useState<TranscriptionState>(_defaultState());
  const unmountedRef = useRef(false);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const handleWebSocketMessage = (channel: string, data: any) => {
    if (unmountedRef.current) return;

    const segment = data.segment;
    if (segment && segment.text) {
      const transcriptionSegment = new TranscriptionSegment();
      transcriptionSegment.text = segment.text;
      transcriptionSegment.startSec = segment.startSec;
      transcriptionSegment.endSec = segment.endSec;
      transcriptionSegment.stable = segment.stable;
      transcriptionSegment.track = segment.track;

      setState((prevState) => {
        let newLastStableTimestamp = prevState.lastStableTimestamp;

        if (
          transcriptionSegment.stable &&
          transcriptionSegment.startSec > newLastStableTimestamp
        ) {
          newLastStableTimestamp = transcriptionSegment.startSec;
        }

        const updatedSegments = [...prevState.segments, transcriptionSegment];

        const filteredSegments = updatedSegments.filter((seg) => {
          return seg.stable || seg.startSec > newLastStableTimestamp;
        });

        return {
          ...prevState,
          segments: filteredSegments,
          lastStableTimestamp: newLastStableTimestamp,
          streamStarted: true,
        };
      });
    }
  };

  useEffect(() => {
    if (!videoRoom.transcriptionEnabled || state.initialLoadComplete) {
      return;
    }

    const loadInitialTranscriptions = async () => {
      const resultData = await request({
        uuid: videoRoom.uuid,
        tracks: tracks.length > 0 ? tracks : [],
        maxTimestampProcessed: 0,
        startTimeOffset: -1,
      });

      if (unmountedRef.current || !resultData) return;

      setState((prevState) => ({
        ...prevState,
        segments: resultData.segments || [],
        complete: resultData.complete,
        streamStarted: resultData.segments.length > 0,
        initialLoadComplete: true,
      }));
    };

    loadInitialTranscriptions();
  }, [videoRoom.uuid]);

  useEffect(() => {
    if (!videoRoom.transcriptionEnabled) {
      return;
    }
    const channel = `video-transcriptions-${videoRoom.uuid}`;
    registerHandler(channel, handleWebSocketMessage);
    return () => {
      unregisterHandler(channel, handleWebSocketMessage);
    };
  }, [
    videoRoom.uuid,
    videoRoom.transcriptionEnabled,
    registerHandler,
    unregisterHandler,
  ]);

  useEffect(() => {
    setState(_defaultState());
  }, [videoRoom.uuid]);

  return {
    segments: state.segments,
    complete: state.complete,
    streamStarted: state.streamStarted,
    transcriptionFailed: state.transcriptionFailed,
  };
};
